import { useCallback, useState } from 'react';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import { REQUEST_STATUS } from '@ubeya/shared/constants';
import {
  LEAVE_ERROR_EMPLOYEE_BOOKED_ON_DATES,
  LEAVE_ERROR_INSUFFICIENT_FUNDS_WILL_GO_NEGATIVE,
  LEAVE_ERROR_WILL_EXCEED_MONTHLY_EARNINGS_LIMIT
} from '@ubeya/shared/constants/leave';

const useLeaveRequestApproval = ({ updateRequestStatus, buildRequestDisplay, onApproved } = {}) => {
  const { accountId } = useAccount();
  const [pendingUnbook, setPendingUnbook] = useState(null);
  const [pendingNegative, setPendingNegative] = useState(null);
  const [pendingEarningsLimit, setPendingEarningsLimit] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const submitApprove = useCallback(
    async (
      requestId,
      { unbookConflictingShifts = false, overrideInsufficientBalance = false, overrideEarningsLimit = false } = {}
    ) => {
      const options = { unbookConflictingShifts, overrideInsufficientBalance, overrideEarningsLimit };
      try {
        await updateRequestStatus({
          accountId,
          requestId,
          status: REQUEST_STATUS.APPROVED,
          unbookConflictingShifts: unbookConflictingShifts || undefined,
          overrideInsufficientBalance: overrideInsufficientBalance || undefined,
          overrideEarningsLimit: overrideEarningsLimit || undefined
        });
        onApproved?.(requestId);
      } catch (error) {
        const errorData = error?.response?.data;
        const code = errorData?.errorCode;

        if (!overrideInsufficientBalance && code === LEAVE_ERROR_INSUFFICIENT_FUNDS_WILL_GO_NEGATIVE) {
          setPendingNegative({
            requestId,
            options,
            balance: errorData?.params?.balance,
            expectedBalance: errorData?.params?.expectedBalance,
            balanceUnit: errorData?.params?.unit,
            ...(buildRequestDisplay?.(requestId) || {})
          });
          return;
        }

        if (!overrideEarningsLimit && code === LEAVE_ERROR_WILL_EXCEED_MONTHLY_EARNINGS_LIMIT) {
          setPendingEarningsLimit({ requestId, options });
          return;
        }

        if (code === LEAVE_ERROR_EMPLOYEE_BOOKED_ON_DATES) {
          setPendingUnbook({
            requestId,
            options,
            shifts: errorData?.params?.shifts || [],
            balance: errorData?.params?.balance,
            expectedBalance: errorData?.params?.expectedBalance,
            balanceUnit: errorData?.params?.unit,
            ...(buildRequestDisplay?.(requestId) || {})
          });
        }
      }
    },
    [accountId, updateRequestStatus, buildRequestDisplay, onApproved]
  );

  const approveRequest = useCallback((requestId) => submitApprove(requestId), [submitApprove]);

  const confirmPending = useCallback(
    async (pending, setPending, acknowledged) => {
      if (!pending || confirming) {
        return;
      }
      const { requestId, options } = pending;
      setConfirming(true);
      try {
        await submitApprove(requestId, { ...options, ...acknowledged });
      } finally {
        setConfirming(false);
      }
      setPending(null);
    },
    [confirming, submitApprove]
  );

  const confirmNegative = useCallback(
    () => confirmPending(pendingNegative, setPendingNegative, { overrideInsufficientBalance: true }),
    [confirmPending, pendingNegative]
  );

  const confirmEarningsLimit = useCallback(
    () => confirmPending(pendingEarningsLimit, setPendingEarningsLimit, { overrideEarningsLimit: true }),
    [confirmPending, pendingEarningsLimit]
  );

  const confirmUnbook = useCallback(
    () => confirmPending(pendingUnbook, setPendingUnbook, { unbookConflictingShifts: true }),
    [confirmPending, pendingUnbook]
  );

  const closeNegative = useCallback(() => setPendingNegative(null), []);
  const closeEarningsLimit = useCallback(() => setPendingEarningsLimit(null), []);
  const closeUnbook = useCallback(() => setPendingUnbook(null), []);

  return {
    pendingNegative,
    pendingEarningsLimit,
    pendingUnbook,
    confirming,
    submitApprove,
    approveRequest,
    confirmNegative,
    confirmEarningsLimit,
    confirmUnbook,
    closeNegative,
    closeEarningsLimit,
    closeUnbook
  };
};

export default useLeaveRequestApproval;
